import { useState, useEffect, createContext, useContext } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile
} from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { auth, googleProvider, db } from '../utils/firebase';
import { setSentryUser } from '../utils/sentry';

const AuthContext = createContext(null);

// Helper to extract first name from display name
function getFirstName(displayName) {
  if (!displayName) return '';
  return displayName.split(' ')[0];
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);

  useEffect(() => {
    let profileUnsubscribe = null;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        setError(null);

        // Set user context for error tracking
        setSentryUser(firebaseUser);

        // Check if user document exists, create if not
        const userDocRef = doc(db, 'users', firebaseUser.uid);

        try {
          const userDoc = await getDoc(userDocRef);

          if (!userDoc.exists()) {
            // First-time user - create their profile
            const newUserProfile = {
              uid: firebaseUser.uid,
              email: firebaseUser.email?.toLowerCase(),
              displayName: firebaseUser.displayName || '',
              firstName: getFirstName(firebaseUser.displayName),
              managerId: null,
              supervisorIds: [],
              taskManagementEnabled: true,
              status: 'active',
              createdAt: new Date(),
              updatedAt: new Date(),
              // Onboarding tracking
              onboarding: {
                completedAt: null,
                skippedAt: null,
                currentStep: 'welcome',
                completedSteps: [],
                brainDumpCount: 0,
                calendarConnected: false,
                firstDailyReviewAt: null,
                firstWeeklyReviewAt: null
              },
              // Feature discovery tracking for coach marks
              discoveredFeatures: {
                inboxCapture: false,
                quickAdd: false,
                statusMeanings: false,
                timeBlocking: false,
                dailyReview: false,
                weeklyReview: false,
                contexts: false,
                oneOnOnes: false,
                habits: false
              }
            };

            await setDoc(userDocRef, newUserProfile);
            setUserProfile(newUserProfile);
            setIsNewUser(true); // Flag for welcome flow
          } else {
            setIsNewUser(false);
          }

          // Subscribe to real-time updates on user profile
          profileUnsubscribe = onSnapshot(
            userDocRef,
            (doc) => {
              if (doc.exists()) {
                setUserProfile({ id: doc.id, ...doc.data() });
              }
            },
            (err) => {
              console.error('Error listening to user profile:', err);
              // Don't fail completely - we already have the profile from getDoc
            }
          );

        } catch (err) {
          console.error('Error loading user profile:', err);
          setError('Failed to load user profile');
        }

        setLoading(false);
      } else {
        // Clean up profile subscription
        if (profileUnsubscribe) {
          profileUnsubscribe();
          profileUnsubscribe = null;
        }
        setUser(null);
        setUserProfile(null);
        setIsNewUser(false);
        setLoading(false);

        // Clear user context for error tracking
        setSentryUser(null);
      }
    });

    return () => {
      unsubscribe();
      if (profileUnsubscribe) {
        profileUnsubscribe();
      }
    };
  }, []);

  // Google OAuth login
  const loginWithGoogle = async () => {
    try {
      setError(null);
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Email/password sign up
  const signUp = async (email, password, displayName) => {
    try {
      setError(null);
      const result = await createUserWithEmailAndPassword(auth, email, password);

      // Update profile with display name
      if (displayName) {
        await updateProfile(result.user, { displayName });
      }

      // Send verification email
      await sendEmailVerification(result.user);

      return result.user;
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    }
  };

  // Email/password login
  const loginWithEmail = async (email, password) => {
    try {
      setError(null);
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    }
  };

  // Send password reset email
  const resetPassword = async (email) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    }
  };

  // Resend verification email
  const resendVerificationEmail = async () => {
    try {
      setError(null);
      if (user && !user.emailVerified) {
        await sendEmailVerification(user);
      }
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      setError(err.message);
    }
  };

  // Clear the new user flag (after showing welcome)
  const clearNewUserFlag = () => {
    setIsNewUser(false);
  };

  // All authenticated users are now admins of their own data
  const isAdmin = !!user && !!userProfile;

  return (
    <AuthContext.Provider value={{
      user,
      userProfile,
      isAdmin,
      loading,
      error,
      isNewUser,
      // Auth methods
      login: loginWithGoogle, // Keep for backwards compatibility
      loginWithGoogle,
      loginWithEmail,
      signUp,
      resetPassword,
      resendVerificationEmail,
      logout,
      clearNewUserFlag
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

// Helper to convert Firebase auth error codes to user-friendly messages
function getAuthErrorMessage(errorCode) {
  const errorMessages = {
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/operation-not-allowed': 'Email/password accounts are not enabled.',
    'auth/weak-password': 'Password should be at least 6 characters.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/invalid-credential': 'Invalid email or password.',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
    'auth/network-request-failed': 'Network error. Please check your connection.',
    'auth/popup-closed-by-user': 'Sign in was cancelled.',
    'auth/requires-recent-login': 'Please sign in again to complete this action.'
  };

  return errorMessages[errorCode] || 'An error occurred. Please try again.';
}
